/**
 * What a project shows while it opens (Canvas, before its configuration is there): "Loading project…", or, when the
 * person's sandbox sleeps and is waking, the same "Waking your sandbox…" card as the Projects page (SandboxWaking). It
 * watches the sandbox from the first moment (`watch`): the project's first requests (revisions, configuration) may not
 * go to the sandbox itself, so waiting for one of them to be slow (utils/sandboxPending.js) left a bare spinner.
 * `onAwake`: the sandbox it saw waking is up (useSandboxWake's AWAKE_EVENT), e.g. load what failed meanwhile again.
 *
 * Still loading after SLOW_MS: the project is asked for again (`onAwake`) every RETRY_MS until it opens. The calm
 * "We're coming back soon" card (Flow is being updated) only while Flow itself is down (utils/flowOutage.js: the
 * project's own requests got 502/503/504 or no answer); a preview's or a connector's 503, or a service that is not
 * running, is not Flow being updated, so otherwise it says "Still opening the project…".
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SandboxWaking from './SandboxWaking';
import { AWAKE_EVENT } from '../../hooks/useSandboxWake';
import { FLOW_OUTAGE_EVENT, isFlowDown } from '../../utils/flowOutage';

export const SLOW_MS = 6000;
export const RETRY_MS = 8000;

export default function ProjectLoading({ sandbox = true, onAwake = undefined }) {
  const [slow, setSlow] = useState(false);
  const [flowDown, setFlowDown] = useState(isFlowDown);
  useEffect(() => {
    const onChange = () => setFlowDown(isFlowDown());
    window.addEventListener(FLOW_OUTAGE_EVENT, onChange);
    return () => window.removeEventListener(FLOW_OUTAGE_EVENT, onChange);
  }, []);
  useEffect(() => {
    if (!onAwake) { return undefined; }
    window.addEventListener(AWAKE_EVENT, onAwake);
    return () => window.removeEventListener(AWAKE_EVENT, onAwake);
  }, [onAwake]);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!slow || !onAwake) { return undefined; }
    const timer = setInterval(() => onAwake(), RETRY_MS);
    return () => clearInterval(timer);
  }, [slow, onAwake]);
  const fallback = slow && flowDown ? (
    <div className="preview-loading project-coming-back" data-testid="project-coming-back" role="status">
      <div className="loading-spinner" />
      <h3>We're coming back soon</h3>
      <p>Flow is being updated or is starting up. Wait a moment: your project opens by itself.</p>
    </div>
  ) : (
    <div className="preview-loading" data-testid="project-loading">
      <div className="loading-spinner" />
      <p>{slow ? 'Still opening the project…' : 'Loading project…'}</p>
    </div>
  );
  return <SandboxWaking fallback={fallback} watch={sandbox} />;
}

ProjectLoading.propTypes = {
  // A sandbox (vfs-backed) project: watch the sandbox while it loads.
  sandbox: PropTypes.bool,
  onAwake: PropTypes.func,
};
